/**
 * JournalEnvoisPage — le journal des envois : courriels ET SMS (2026-09-21)
 *
 * Demande du commanditaire : « il faut que les super_admin ainsi que les admin aient un log
 * détaillé, aussi pour les mails et pour les SMS (c'était la force de n8n…) ». n8n montrait
 * chaque exécution, son entrée, sa sortie, son erreur. Cet écran rend la même chose, sans
 * n8n : un seul tableau, les deux canaux mêlés par date.
 *
 * Ce que l'écran ne fait PAS : afficher un mot de passe ou un lien de réinitialisation. Le
 * corps arrive déjà rédigé du serveur (`body_redacted`) — il n'y a rien à masquer ici, et
 * rien à démasquer non plus.
 */

import { useCallback, useEffect, useState } from 'react';
import { useHasPermission } from '../SupabaseWrapper';

const API_BASE = window.location.origin;

type Canal = 'tous' | 'courriel' | 'sms';

interface LigneEnvoi {
  canal: 'courriel' | 'sms';
  id: string;
  date: string;
  destinataire: string | null;
  objet: string | null;
  corps: string | null;
  type: string | null;
  statut: string | null;
  fournisseur: string | null;
  id_fournisseur: string | null;
  erreur: string | null;
  intervention_id: string | null;
  piece: string | null;
  cout: number | null;
}

interface MetaEnvois {
  portee: string;
  canal?: Canal;
  limite?: number;
  courriels?: number;
  sms?: number;
  tronque?: boolean;
  avertissement?: string;
}

interface JournalEnvoisPageProps { refreshTrigger?: number }

// Les couleurs disent l'état d'un coup d'œil : un échec ne doit pas se lire, il doit sauter.
const COULEUR_STATUT: Record<string, { fond: string; texte: string; libelle: string }> = {
  sent:      { fond: '#DCFCE7', texte: '#166534', libelle: 'Envoyé' },
  delivered: { fond: '#DCFCE7', texte: '#166534', libelle: 'Livré' },
  pending:   { fond: '#FEF3C7', texte: '#92400E', libelle: 'En attente' },
  sending:   { fond: '#FEF3C7', texte: '#92400E', libelle: 'En cours' },
  failed:    { fond: '#FEE2E2', texte: '#991B1B', libelle: 'Échec' },
  undelivered: { fond: '#FEE2E2', texte: '#991B1B', libelle: 'Non livré' },
  expired:   { fond: '#FEE2E2', texte: '#991B1B', libelle: 'Expiré' },
};

const TYPES_LISIBLES: Record<string, string> = {
  bon_intervention: "Bon d'intervention",
  password_reset: 'Réinitialisation',
  transactional: 'Transactionnel',
  otp: 'Code à usage unique',
};

const dateLisible = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
};

function JournalEnvoisPage({ refreshTrigger = 0 }: JournalEnvoisPageProps) {
  const peutVoir = useHasPermission('notifications.view');

  const [lignes, setLignes] = useState<LigneEnvoi[]>([]);
  const [meta, setMeta] = useState<MetaEnvois | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [detail, setDetail] = useState<LigneEnvoi | null>(null);

  const [canal, setCanal] = useState<Canal>('tous');
  const [statut, setStatut] = useState<string>('tous');
  const [depuis, setDepuis] = useState<string>('');
  const [recherche, setRecherche] = useState<string>('');

  const charger = useCallback(async () => {
    const token = localStorage.getItem('sso_token');
    setChargement(true);
    setErreur(null);
    try {
      const p = new URLSearchParams();
      if (canal !== 'tous') p.set('canal', canal);
      if (statut !== 'tous') p.set('statut', statut);
      if (depuis) p.set('depuis', depuis);
      if (recherche.trim()) p.set('recherche', recherche.trim());
      p.set('limite', '100');

      console.log('[JournalEnvois] Chargement —', p.toString());
      const reponse = await fetch(`${API_BASE}/api/notifications/envois?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const corps = await reponse.json();
      if (!reponse.ok || !corps.success) {
        throw new Error(corps?.error?.message || `HTTP ${reponse.status}`);
      }
      setLignes(corps.data.items || []);
      setMeta(corps.data.meta || null);
      console.log(`[JournalEnvois] ${(corps.data.items || []).length} ligne(s), portée ${corps.data.meta?.portee}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[JournalEnvois] Erreur:', message);
      setErreur(message);
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [canal, statut, depuis, recherche]);

  useEffect(() => { if (peutVoir) charger(); }, [peutVoir, charger, refreshTrigger]);

  if (!peutVoir) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
        <p style={{ fontSize: '15px' }}>
          Vous n'avez pas l'autorisation de consulter le journal des envois.
        </p>
      </div>
    );
  }

  const champ: React.CSSProperties = {
    padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px',
    fontSize: '14px', backgroundColor: '#fff', color: '#0f172a',
  };
  const cellule: React.CSSProperties = {
    padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontSize: '13px',
    verticalAlign: 'top', textAlign: 'left',
  };
  const entete: React.CSSProperties = {
    ...cellule, fontWeight: 600, color: '#475569', backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        <select value={canal} onChange={(e) => setCanal(e.target.value as Canal)} style={champ}>
          <option value="tous">Les deux canaux</option>
          <option value="courriel">Courriels</option>
          <option value="sms">SMS</option>
        </select>
        <select value={statut} onChange={(e) => setStatut(e.target.value)} style={champ}>
          <option value="tous">Tous les états</option>
          <option value="sent">Envoyés</option>
          <option value="delivered">Livrés</option>
          <option value="failed">En échec</option>
          <option value="pending">En attente</option>
        </select>
        <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)} style={champ}
               title="À partir de cette date" />
        <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
               placeholder="Destinataire (courriel ou numéro)" style={{ ...champ, minWidth: '240px' }} />
        <button onClick={charger} disabled={chargement}
                style={{ ...champ, cursor: chargement ? 'wait' : 'pointer', fontWeight: 600,
                         backgroundColor: '#1e293b', color: '#fff', border: 'none' }}>
          {chargement ? 'Chargement…' : 'Actualiser'}
        </button>
      </div>

      {/* Ce que la portée laisse voir — dit, jamais deviné */}
      {meta?.avertissement && (
        <div style={{ padding: '12px 14px', marginBottom: '14px', borderRadius: '8px',
                      backgroundColor: '#FEF3C7', color: '#92400E', fontSize: '14px' }}>
          {meta.avertissement}
        </div>
      )}

      {erreur && (
        <div style={{ padding: '12px 14px', marginBottom: '14px', borderRadius: '8px',
                      backgroundColor: '#FEE2E2', color: '#991B1B', fontSize: '14px' }}>
          Le journal n'a pas pu être lu : {erreur}
        </div>
      )}

      {meta && !meta.avertissement && (
        <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px' }}>
          {lignes.length} envoi(s) — {meta.courriels ?? 0} courriel(s), {meta.sms ?? 0} SMS
          {meta.tronque ? ` · liste tronquée à ${meta.limite}, affinez les filtres` : ''}
        </p>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={entete}>Date</th>
              <th style={entete}>Canal</th>
              <th style={entete}>Destinataire</th>
              <th style={entete}>Objet / Motif</th>
              <th style={entete}>Type</th>
              <th style={entete}>État</th>
              <th style={entete}>Fournisseur</th>
              <th style={entete}>Coût</th>
              <th style={entete}></th>
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 && !chargement && (
              <tr><td colSpan={9} style={{ ...cellule, textAlign: 'center', color: '#94a3b8', padding: '28px' }}>
                Aucun envoi pour ces critères.
              </td></tr>
            )}
            {lignes.map((l) => {
              const c = COULEUR_STATUT[l.statut || ''] || { fond: '#f1f5f9', texte: '#475569', libelle: l.statut || '—' };
              return (
                <tr key={`${l.canal}-${l.id}`}>
                  <td style={{ ...cellule, whiteSpace: 'nowrap' }}>{dateLisible(l.date)}</td>
                  <td style={cellule}>{l.canal === 'sms' ? 'SMS' : 'Courriel'}</td>
                  <td style={cellule}>{l.destinataire || '—'}</td>
                  <td style={{ ...cellule, maxWidth: '320px' }}>
                    {l.objet || (l.corps ? l.corps.slice(0, 80) : '—')}
                    {l.erreur && (
                      <div style={{ color: '#991B1B', fontSize: '12px', marginTop: '4px' }}>{l.erreur}</div>
                    )}
                  </td>
                  <td style={cellule}>{TYPES_LISIBLES[l.type || ''] || l.type || '—'}</td>
                  <td style={cellule}>
                    <span style={{ backgroundColor: c.fond, color: c.texte, padding: '3px 8px',
                                   borderRadius: '10px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {c.libelle}
                    </span>
                  </td>
                  <td style={{ ...cellule, fontSize: '12px', color: '#64748b' }}>{l.fournisseur || '—'}</td>
                  <td style={{ ...cellule, whiteSpace: 'nowrap' }}>
                    {typeof l.cout === 'number' ? `${l.cout.toFixed(4)} €` : '—'}
                  </td>
                  <td style={cellule}>
                    <button onClick={() => setDetail(l)}
                            style={{ border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#fff',
                                     padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: '#0f172a' }}>
                      Détail
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Détail — ce que n8n montrait : l'entrée, la sortie, l'erreur */}
      {detail && (
        <div onClick={() => setDetail(null)}
             style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '22px', maxWidth: '680px',
                        width: '100%', maxHeight: '82vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '17px', color: '#0f172a' }}>
              {detail.canal === 'sms' ? 'SMS' : 'Courriel'} du {dateLisible(detail.date)}
            </h3>
            {[
              ['Destinataire', detail.destinataire],
              ['Objet', detail.objet],
              ['Type', TYPES_LISIBLES[detail.type || ''] || detail.type],
              ['État', (COULEUR_STATUT[detail.statut || ''] || { libelle: detail.statut }).libelle],
              ['Motif de l\'échec', detail.erreur],
              ['Fournisseur', detail.fournisseur],
              ['Identifiant chez le fournisseur', detail.id_fournisseur],
              ['Pièce jointe', detail.piece],
              ['Ordre de réparation', detail.intervention_id],
              ['Coût', typeof detail.cout === 'number' ? `${detail.cout.toFixed(4)} €` : null],
            ].filter(([, v]) => v).map(([k, v]) => (
              <p key={String(k)} style={{ margin: '0 0 8px', fontSize: '14px', color: '#334155' }}>
                <strong style={{ color: '#475569' }}>{k} :</strong> {String(v)}
              </p>
            ))}
            {detail.corps && (
              <>
                <p style={{ margin: '14px 0 6px', fontSize: '13px', color: '#64748b' }}>
                  Message envoyé — secrets retirés avant enregistrement :
                </p>
                <pre style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px',
                              padding: '12px', fontSize: '12px', whiteSpace: 'pre-wrap', color: '#0f172a' }}>
                  {detail.corps}
                </pre>
              </>
            )}
            {!detail.corps && detail.canal === 'courriel' && (
              <p style={{ margin: '14px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                Le corps d'un bon d'intervention n'est pas conservé : c'est un gabarit identique
                rempli de données client.
              </p>
            )}
            <button onClick={() => setDetail(null)}
                    style={{ marginTop: '18px', padding: '9px 16px', border: 'none', borderRadius: '6px',
                             backgroundColor: '#1e293b', color: '#fff', fontSize: '14px', cursor: 'pointer' }}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default JournalEnvoisPage;
